import { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon, FunnelIcon } from '@heroicons/react/20/solid';
import { Button } from '../buttons/Button';

/**
 * Popover de filtro multi-selección con checkboxes custom.
 *
 * @param {object}   state                Objeto de estado que contiene el filtro (p. ej. formData).
 * @param {function} setState             Setter para actualizar el estado.
 * @param {string}   property             Llave dentro de state donde vive el array de valores seleccionados.
 * @param {Array}    options              [{ value, label, description?, dotClass? }]
 * @param {string}   title                Título del header.
 * @param {string}   subtitle             Subtítulo del header.
 * @param {Element}  icon                 Componente de icono (heroicon).
 * @param {string}   placeholderLabel     Texto del botón cuando no hay selección.
 * @param {function} handleApllyFilters   Callback que recibe el payload actualizado.
 * @param {function} handleRemoveFilter   Callback para limpiar el filtro.
 */
export const PopoverCheckboxFilter = ({
	state,
	setState,
	property,
	options = [],
	title = 'Filtrar',
	subtitle,
	icon: Icon = FunnelIcon,
	placeholderLabel,
	handleApllyFilters,
	handleRemoveFilter,
	hideActionButtons = false,
}) => {
	const appliedValues = Array.isArray(state?.[property]) ? state[property] : [];
	const [selected, setSelected] = useState(appliedValues);

	useEffect(() => {
		setSelected(Array.isArray(state?.[property]) ? state[property] : []);
	}, [state, property]);

	const syncSelectionToState = (nextSelected) => {
		if (!hideActionButtons) return;
		setState({
			...state,
			[property]: nextSelected,
		});
	};

	const toggle = (value) => {
		setSelected((prev) => {
			const nextSelected = prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value];
			syncSelectionToState(nextSelected);
			return nextSelected;
		});
	};

	const handleSelectAll = () => {
		const nextSelected = options.map((opt) => opt.value);
		setSelected(nextSelected);
		syncSelectionToState(nextSelected);
	};

	const handleToggleSelectAll = () => {
		if (allSelected) {
			setSelected([]);
			syncSelectionToState([]);
			return;
		}
		handleSelectAll();
	};

	const handleApply = (close) => {
		const next = { ...state, [property]: selected };
		setState(next);
		handleApllyFilters?.(next);
		close?.();
	};

	const handleClear = (close) => {
		setSelected([]);
		const next = { ...state, [property]: [] };
		setState(next);
		handleRemoveFilter?.(next);
		close?.();
	};

	const emptyLabel = placeholderLabel || title;
	const summary = () => {
		if (appliedValues.length === 0) return emptyLabel;
		if (appliedValues.length === 1) {
			const found = options.find((opt) => opt.value === appliedValues[0]);
			return found ? found.label : emptyLabel;
		}
		return `${appliedValues.length} seleccionados`;
	};

	const hasApplied = appliedValues.length > 0;
	const allSelected = options.length > 0 && selected.length === options.length;

	return (
		<div className='flex w-full text-center justify-center'>
			<Menu as='div' className='w-full relative inline-block text-left'>
				<div>
					<Menu.Button
						className={`inline-flex w-full justify-center items-center rounded-md bg-white px-4 py-2 text-md font-medium shadow-sm ring-1 ring-inset ${hasApplied ? 'ring-gray-400 text-gray-900' : 'ring-gray-300 text-gray-700'} hover:bg-gray-50 hover:text-gray-900 hover:ring-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`}
					>
						<Icon className='mr-2 h-4 w-4 text-gray-500' aria-hidden='true' />
						{summary()}
						{
							hasApplied && (
								<span className='ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] font-semibold text-white'>
									{appliedValues.length}
								</span>
							)
						}
						<ChevronDownIcon className='-mr-1 ml-2 h-5 w-5 text-gray-500' aria-hidden='true' />
					</Menu.Button>
				</div>

				<Transition
					as={Fragment}
					enter='transition ease-out duration-150'
					enterFrom='transform opacity-0 scale-95 translate-y-1'
					enterTo='transform opacity-100 scale-100 translate-y-0'
					leave='transition ease-in duration-100'
					leaveFrom='transform opacity-100 scale-100 translate-y-0'
					leaveTo='transform opacity-0 scale-95 translate-y-1'
				>
					<Menu.Items className='absolute right-0 mt-2 w-full md:w-[340px] origin-top-right overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-200 focus:outline-none z-50'>
						{({ close }) => (
							<>
								<div className='flex items-start gap-3 bg-gradient-to-br from-indigo-50/70 via-white to-white px-5 py-4 border-b border-gray-100'>
									<div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600'>
										<Icon className='h-5 w-5' aria-hidden='true' />
									</div>
									<div className='text-left'>
										<h3 className='text-sm font-semibold text-gray-900'>{title}</h3>
										{
											subtitle && (
												<p className='mt-0.5 text-xs text-gray-500 leading-snug'>
													{subtitle}
												</p>
											)
										}
									</div>
								</div>

								<div className='flex items-center justify-between px-5 pt-4'>
									<span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
										Opciones
									</span>
									<button
										type='button'
										onClick={handleToggleSelectAll}
										className='text-xs font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none'
									>
										{allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
									</button>
								</div>

								<div className='flex flex-col gap-2 px-5 pt-2 pb-4'>
									{
										options.map((option) => {
											const checked = selected.includes(option.value);
											return (
												<label
													key={option.value}
													htmlFor={`popoverCheckboxFilter-${property}-${option.value}`}
													className={`group flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-all duration-150 ${
														checked
															? 'border-indigo-500 bg-indigo-50/60 ring-1 ring-indigo-200'
															: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
													}`}
												>
													<input
														id={`popoverCheckboxFilter-${property}-${option.value}`}
														type='checkbox'
														checked={checked}
														onChange={() => toggle(option.value)}
														className='sr-only'
													/>
													<span
														className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all duration-150 ${
															checked
																? 'border-indigo-600 bg-indigo-600 shadow-sm'
																: 'border-gray-300 bg-white group-hover:border-gray-400'
														}`}
														aria-hidden='true'
													>
														<CheckIcon
															className={`h-3.5 w-3.5 text-white transition-all duration-150 ${
																checked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
															}`}
															strokeWidth={3}
														/>
													</span>
													<div className='min-w-0 flex-1 text-left'>
														<div className='flex items-center gap-2'>
															{
																option.dotClass && (
																	<span className={`inline-block h-2 w-2 shrink-0 rounded-full ${option.dotClass}`}></span>
																)
															}
															<span className={`text-sm font-semibold capitalize ${checked ? 'text-indigo-700' : 'text-gray-800'}`}>
																{option.label}
															</span>
														</div>
														{
															option.description && (
																<p className='mt-0.5 text-[11px] text-gray-500 leading-snug'>
																	{option.description}
																</p>
															)
														}
													</div>
												</label>
											);
										})
									}
								</div>

								<div className='flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3'>
									<span className='text-xs text-gray-500'>
										{
											selected.length === 0
												? 'Ningún elemento seleccionado'
												: `${selected.length} seleccionado${selected.length === 1 ? '' : 's'}`
										}
									</span>
									{
										!hideActionButtons && (
											<div className='flex items-center gap-2'>
												<Button variant='secondary' onClick={() => handleClear(close)}>
													Limpiar
												</Button>
												<Button variant='primary' onClick={() => handleApply(close)}>
													Aplicar
												</Button>
											</div>
										)
									}
								</div>
							</>
						)}
					</Menu.Items>
				</Transition>
			</Menu>
		</div>
	);
};
